/**
 * Form Component
 *
 * Note: Form is a special container component that has field children.
 * Fields need to be mapped to table column IDs.
 */

import { BuilderAction } from '@/core/ai/app-builder/action.structure'
import type { IBody, IBodySettings } from '@makyoapp/makyo-sdk/models/AppBuilder'

import { IBuilderComponent } from '../../base'
import {
  formatColorFormat,
  formatComponentAlignmentFormat,
  formatCornerRadiusFormat,
  formatFromVariableText,
  formatHorizontalAlignmentFormat,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import { BaseAiComponentInput } from '../../registry'
import type {
  ColorAdaptiveValue,
  ColorType,
  ColorVariant,
  ComponentAlignment,
  GradientDirection,
  HorizontalAlignment,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseColorFormat,
  parseCornerRadiusFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import formTemplate from './form.template.json'

export interface IFormComponentSettings {
  // Data settings
  'form-data-source': string
  'form-data-comp-title': VariableTextValue

  // Design settings
  'form-design-title-color': ColorAdaptiveValue<ColorType>
  'form-design-title-color-solid': ColorAdaptiveValue<ColorVariant>
  'form-design-title-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'form-design-title-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'form-design-title-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'form-design-label-color': ColorAdaptiveValue<ColorType>
  'form-design-label-color-solid': ColorAdaptiveValue<ColorVariant>
  'form-design-label-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'form-design-label-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'form-design-label-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'form-design-field-shape': string
  'form-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'form-design-h-align': ResponsiveValue<ComponentAlignment>
  'form-design-comp-title-align': ResponsiveValue<HorizontalAlignment>
  'form-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'form-design-padding': ResponsiveValue<SpacingInputValue>
  'form-design-field-margin': ResponsiveValue<SpacingInputValue>
  'form-design-comp-title-margin': ResponsiveValue<SpacingInputValue>
  'form-design-typography': {
    'form-design-label-typography': ResponsiveValue<TypographyValue>
    'form-design-input-typography': ResponsiveValue<TypographyValue>
    'form-design-help-typography': ResponsiveValue<TypographyValue>
    'form-design-comp-title-typography': ResponsiveValue<TypographyValue>
  }
}

export interface IFormComponent extends IBuilderComponent {
  componentId: 'form'
  groupId: 'container'
  settings: IFormComponentSettings
}

/**
 * Form AI Input
 * Note: Form uses the `children` property (from BaseAiComponentInput) for fields
 * Each child component should have a `columnId` property to map to table columns
 */
export interface AiFormInput extends BaseAiComponentInput {
  tableId: string // Required: The data source table ID
  compTitle?: string[] // Format: ["text:Contact Form"] or ["text:Submit ", "cr:formType"]
  submitButton?: {
    label?: string[] // Format: ["text:Submit"] or ["text:Save ", "cr:itemType"]
    backgroundColor?: string // Button background color
    size?: string // Button size
    cornerRadius?: string // Button corner radius
    actions?: BuilderAction[] // Actions to run on submit
  } | null // Set to null to disable submit button
  // Styling properties
  titleColor?: string // Format: "solid:primary-40" or "adaptive"
  labelColor?: string // Format: "solid:primary-30" or "adaptive"
  fieldCornerRadius?: string // Corner radius for fields
  width?: string // Format: "w:100%" or "w:600px" or "w:100% de:w:600px"
  alignment?: string // Format: "center" or "left"
  titleAlignment?: string // Format: "center" or "left"
  margin?: string // Format: "m:16px"
  padding?: string // Format: "p:20px"
  fieldMargin?: string // Margin between fields
  titleMargin?: string // Margin for title
  typography?: {
    title?: string // Title typography
    label?: string // Label typography
    input?: string // Input typography
    help?: string // Help text typography
  }
}

const FORM_DEFAULT_TEMPLATE = formTemplate as any

export class FormComp {
  getTemplate(): IFormComponentSettings {
    // Extract just the form settings from the template
    const templateSettings = FORM_DEFAULT_TEMPLATE._BodySettings?.[FORM_DEFAULT_TEMPLATE._Body?.[0]?._id]
    return templateSettings || {}
  }

  /**
   * format form component settings to AI form input
   */
  format(
    settings: IFormComponentSettings,
    body: IBody,
    bodySettings: IBodySettings,
    formatChild: (child: IBody, settings: IBodySettings) => any
  ): AiFormInput {
    const template = this.getTemplate()
    const result: AiFormInput = {
      type: 'form',
      tableId: settings['form-data-source'] || '',
    }

    // Component Title
    const compTitle = formatFromVariableText(settings['form-data-comp-title'])
    if (compTitle && compTitle.length > 0) {
      result.compTitle = compTitle
    }

    // Title Color
    const titleColor = formatColorFormat({
      colorType: settings['form-design-title-color'],
      colorSolid: settings['form-design-title-color-solid'],
      colorStartGradient: settings['form-design-title-color-start-gradient'],
      colorEndGradient: settings['form-design-title-color-end-gradient'],
      colorGradientDirection: settings['form-design-title-color-gradient-direction'],
    })
    if (titleColor) {
      result.titleColor = titleColor
    }

    // Label Color
    const labelColor = formatColorFormat({
      colorType: settings['form-design-label-color'],
      colorSolid: settings['form-design-label-color-solid'],
      colorStartGradient: settings['form-design-label-color-start-gradient'],
      colorEndGradient: settings['form-design-label-color-end-gradient'],
      colorGradientDirection: settings['form-design-label-color-gradient-direction'],
    })
    if (labelColor) {
      result.labelColor = labelColor
    }

    // Field Shape
    const fieldShape = formatCornerRadiusFormat(settings['form-design-field-shape'])
    if (fieldShape) {
      result.fieldCornerRadius = fieldShape
    }

    // Width
    if (settings['form-design-width']) {
      const width = formatWidthFormat(settings['form-design-width'] as any)
      if (width) {
        result.width = width
      }
    }

    // Alignment (component alignment - h:start, h:center, h:end, h:parent)
    const alignment = formatComponentAlignmentFormat(settings['form-design-h-align'])
    if (alignment) {
      // Convert from component alignment format (h:center) to simple string (center)
      result.alignment = alignment.replace(/^h:/, '')
    }

    // Title Alignment (horizontal alignment - h:start, h:center, h:end, no parent)
    const titleAlignment = formatHorizontalAlignmentFormat(settings['form-design-comp-title-align'])
    if (titleAlignment) {
      result.titleAlignment = titleAlignment.replace(/^h:/, '')
    }

    // Margin
    if (settings['form-design-margin']) {
      const margin = formatSpacingFormat(settings['form-design-margin'] as any)
      if (margin) {
        result.margin = margin
      }
    }

    // Padding
    const padding = formatSpacingFormat(settings['form-design-padding'])
    if (padding) {
      result.padding = padding
    }

    // Field Margin
    const fieldMargin = formatSpacingFormat(settings['form-design-field-margin'])
    if (fieldMargin) {
      result.fieldMargin = fieldMargin
    }

    // Title Margin
    const titleMargin = formatSpacingFormat(settings['form-design-comp-title-margin'])
    if (titleMargin) {
      result.titleMargin = titleMargin
    }

    // Typography
    const typography = settings['form-design-typography']
    if (typography) {
      const typoResult: any = {}
      if (typography['form-design-comp-title-typography']) {
        const titleTypo = formatTypographyFormat(typography['form-design-comp-title-typography'])
        if (titleTypo) {
          typoResult.title = titleTypo
        }
      }
      if (typography['form-design-label-typography']) {
        const labelTypo = formatTypographyFormat(typography['form-design-label-typography'])
        if (labelTypo) {
          typoResult.label = labelTypo
        }
      }
      if (typography['form-design-input-typography']) {
        const inputTypo = formatTypographyFormat(typography['form-design-input-typography'])
        if (inputTypo) {
          typoResult.input = inputTypo
        }
      }
      if (typography['form-design-help-typography']) {
        const helpTypo = formatTypographyFormat(typography['form-design-help-typography'])
        if (helpTypo) {
          typoResult.help = helpTypo
        }
      }
      if (Object.keys(typoResult).length > 0) {
        result.typography = typoResult
      }
    }

    // Submit Actions (from form-action-submit)
    // Note: Actions are stored in settings but we'll handle them separately
    // The submitButton will be created from the button child component

    // Format form field children
    // Extract submit button from children if it exists (recursively search nested sections)
    const children: any[] = []
    let submitButtonFound = false

    // Recursively search for submit button in nested structures and format children excluding submit button
    const formatChildExcludingSubmitButton = (childBody: IBody): { formatted: any; hasSubmitButton: boolean } => {
      // Check if this is a submit button
      if (childBody._ComponentID === 'button' && childBody._IsSubmit === true) {
        // Extract submit button settings
        const buttonSettings = bodySettings[childBody._id] || {}
        const buttonLabel = formatFromVariableText(buttonSettings['button-data-label'])
        const buttonSize = buttonSettings['button-design-size'] || 'lg'
        const buttonShape = formatCornerRadiusFormat(buttonSettings['button-design-shape'])
        const buttonBgColor = buttonSettings['button-design-color-type-solid']?.phone || undefined
        const buttonActions = buttonSettings['button-action-click'] || []

        result.submitButton = {
          ...(buttonLabel && buttonLabel.length > 0 && { label: buttonLabel }),
          ...(buttonSize !== 'lg' && { size: buttonSize }),
          ...(buttonShape && { cornerRadius: buttonShape }),
          ...(buttonBgColor && { backgroundColor: buttonBgColor }),
          ...(buttonActions.length > 0 && { actions: buttonActions as any }),
        }
        return { formatted: null, hasSubmitButton: true }
      }

      // Format this child normally
      const formatted = formatChild(childBody, bodySettings)
      let hasSubmitButton = false

      // Recursively process nested children (e.g., sections)
      if (childBody._Body && childBody._Body.length > 0 && formatted.children) {
        const filteredChildren: any[] = []
        for (let i = 0; i < childBody._Body.length; i++) {
          const nestedChild = childBody._Body[i]
          const nestedResult = formatChildExcludingSubmitButton(nestedChild)
          if (nestedResult.hasSubmitButton) {
            hasSubmitButton = true
            // Don't add the submit button to children
          } else if (nestedResult.formatted) {
            filteredChildren.push(nestedResult.formatted)
          }
        }
        if (filteredChildren.length > 0) {
          formatted.children = filteredChildren
        } else {
          delete formatted.children
        }
      }

      return { formatted, hasSubmitButton }
    }

    if (body._Body && body._Body.length > 0) {
      for (const child of body._Body) {
        const result = formatChildExcludingSubmitButton(child)
        if (result.hasSubmitButton) {
          submitButtonFound = true
          // Don't add submit button to children, but add other formatted children
          if (result.formatted) {
            children.push(result.formatted)
          }
        } else if (result.formatted) {
          children.push(result.formatted)
        }
      }
    }

    // Only set children if there are any (excluding submit button)
    if (children.length > 0) {
      result.children = children
    }

    // If no submit button was found in children, set submitButton to undefined (will use default)
    if (!submitButtonFound) {
      // submitButton defaults to undefined, which means use default submit button
      // If we want to explicitly disable it, we'd set result.submitButton = null
    }

    return result
  }

  /**
   * parse AI form input to form component settings
   * Note: This only returns the form container settings.
   * The field children are created by the parser.
   */
  parse(input: AiFormInput): IFormComponentSettings {
    const template = this.getTemplate()
    const result = deepMerge(template, {})

    // Data - Table ID (data source)
    if (input.tableId) {
      result['form-data-source'] = input.tableId
    }

    // Data - Title
    if (input.compTitle) {
      result['form-data-comp-title'] = parseToVariableText(input.compTitle)
    }

    // Design - Title Color
    if (input.titleColor) {
      const colorParsed = parseColorFormat(input.titleColor)
      result['form-design-title-color'] = colorParsed.colorType
      result['form-design-title-color-solid'] = colorParsed.colorSolid
      result['form-design-title-color-start-gradient'] = colorParsed.colorStartGradient
      result['form-design-title-color-end-gradient'] = colorParsed.colorEndGradient
      result['form-design-title-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Label Color
    if (input.labelColor) {
      const colorParsed = parseColorFormat(input.labelColor)
      result['form-design-label-color'] = colorParsed.colorType
      result['form-design-label-color-solid'] = colorParsed.colorSolid
      result['form-design-label-color-start-gradient'] = colorParsed.colorStartGradient
      result['form-design-label-color-end-gradient'] = colorParsed.colorEndGradient
      result['form-design-label-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Field Shape
    if (input.fieldCornerRadius) {
      const shapeParsed = parseCornerRadiusFormat(input.fieldCornerRadius)
      result['form-design-field-shape'] = shapeParsed.phone
    }

    // Design - Width
    if (input.width) {
      const widthParsed = parseWidthFormat(input.width)
      result['form-design-width'] = {
        ...widthParsed,
        overflowSetting: result['form-design-width'].overflowSetting,
      }
    }

    // Design - Alignment (ComponentAlignment: 'start' | 'center' | 'end' | 'parent')
    if (input.alignment) {
      const alignmentMap: Record<string, ComponentAlignment> = {
        start: 'start',
        center: 'center',
        end: 'end',
        parent: 'parent',
      }
      result['form-design-h-align'] = {
        phone: alignmentMap[input.alignment] || 'parent',
        'phone-landscape': alignmentMap[input.alignment] || 'parent',
        tablet: alignmentMap[input.alignment] || 'parent',
        'tablet-landscape': alignmentMap[input.alignment] || 'parent',
        desktop: alignmentMap[input.alignment] || 'parent',
        tv: alignmentMap[input.alignment] || 'parent',
      }
    }

    // Design - Title Alignment (HorizontalAlignment: 'start' | 'center' | 'end' | 'parent')
    if (input.titleAlignment) {
      const alignmentMap: Record<string, HorizontalAlignment> = {
        start: 'start',
        center: 'center',
        end: 'end',
        parent: 'parent',
      }
      result['form-design-comp-title-align'] = {
        phone: alignmentMap[input.titleAlignment] || 'center',
        'phone-landscape': alignmentMap[input.titleAlignment] || 'center',
        tablet: alignmentMap[input.titleAlignment] || 'center',
        'tablet-landscape': alignmentMap[input.titleAlignment] || 'center',
        desktop: alignmentMap[input.titleAlignment] || 'center',
        tv: alignmentMap[input.titleAlignment] || 'center',
      }
    }

    // Design - Margin
    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      result['form-design-margin'] = {
        ...marginParsed,
        overflowSetting: result['form-design-margin'].overflowSetting,
      }
    }

    // Design - Padding
    if (input.padding) {
      result['form-design-padding'] = parseSpacingFormat(input.padding)
    }

    // Design - Field Margin
    if (input.fieldMargin) {
      result['form-design-field-margin'] = parseSpacingFormat(input.fieldMargin)
    }

    // Design - Title Margin
    if (input.titleMargin) {
      result['form-design-comp-title-margin'] = parseSpacingFormat(input.titleMargin)
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.title) {
        result['form-design-typography']['form-design-comp-title-typography'] = parseTypographyFormat(
          input.typography.title
        )
      }
      if (input.typography.label) {
        result['form-design-typography']['form-design-label-typography'] = parseTypographyFormat(input.typography.label)
      }
      if (input.typography.input) {
        result['form-design-typography']['form-design-input-typography'] = parseTypographyFormat(input.typography.input)
      }
      if (input.typography.help) {
        result['form-design-typography']['form-design-help-typography'] = parseTypographyFormat(input.typography.help)
      }
    }

    return result
  }
}
